import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { request } from './api';

// ============================================================
// קטלוג התוכניות בממשק. המקור היחיד הוא השרת
// (src/modules/billing/plan-catalog.ts) — GET /billing/plans (מחובר) ו-
// GET /public/billing/plans (דף הנחיתה). FALLBACK_CATALOG הוא העתק לשימוש
// רק כשהשרת לא זמין (דף הנחיתה בלי API), עם אותה גרסה; plan-catalog.test.ts
// מוודא שהחישובים כאן תואמים לו.
// ============================================================

export const planKeySchema = z.enum(['STARTER', 'TEAM', 'GROWTH', 'BUSINESS']);
export type PlanKey = z.infer<typeof planKeySchema>;
export const intervalSchema = z.enum(['MONTHLY', 'ANNUAL']);
export type BillingInterval = z.infer<typeof intervalSchema>;

export const catalogSchema = z.object({
  version: z.string(),
  currency: z.string(),
  vatRateBps: z.number().int(),
  trialDays: z.number().int(),
  trialPlan: planKeySchema,
  graceDays: z.number().int(),
  annualMonthsCharged: z.number().int(),
  extraSeatMonthlyMinor: z.number().int(),
  maxTotalSeats: z.number().int(),
  seatWeights: z.object({ OWNER: z.number(), MANAGER: z.number(), FIELD: z.number(), VIEWER: z.number() }),
  plans: z.array(
    z.object({
      key: planKeySchema,
      name: z.object({ he: z.string(), en: z.string() }),
      seats: z.number().int(),
      monthlyMinor: z.number().int(),
      annualMinor: z.number().int(),
      annualMonthlyEquivalentMinor: z.number().int(),
      aiBudgetMinor: z.number().int(),
      extraSeatsAllowed: z.boolean(),
      maxExtraSeats: z.number().int(),
    }),
  ),
});
export type PlanCatalog = z.infer<typeof catalogSchema>;
export type CatalogPlan = PlanCatalog['plans'][number];

/**
 * העתק של הקטלוג בשרת (גרסה 2026-10). רק לנפילה — הממשק מעדיף את השרת.
 * שמות התוכניות בממשק תמיד מ-i18n (billing:plans.<KEY>.name), לא מכאן.
 */
export const FALLBACK_CATALOG: PlanCatalog = {
  version: '2026-10',
  currency: 'ILS',
  vatRateBps: 1800,
  trialDays: 14,
  trialPlan: 'TEAM',
  graceDays: 7,
  annualMonthsCharged: 10,
  extraSeatMonthlyMinor: 4900,
  maxTotalSeats: 35,
  seatWeights: { OWNER: 1, MANAGER: 1, FIELD: 1, VIEWER: 1 },
  plans: [
    { key: 'STARTER', name: { he: 'Starter', en: 'Starter' }, seats: 3, monthlyMinor: 29900, annualMinor: 299000, annualMonthlyEquivalentMinor: 24917, aiBudgetMinor: 1000, extraSeatsAllowed: false, maxExtraSeats: 0 },
    { key: 'TEAM', name: { he: 'Team', en: 'Team' }, seats: 5, monthlyMinor: 44900, annualMinor: 449000, annualMonthlyEquivalentMinor: 37417, aiBudgetMinor: 2500, extraSeatsAllowed: false, maxExtraSeats: 0 },
    { key: 'GROWTH', name: { he: 'Growth', en: 'Growth' }, seats: 10, monthlyMinor: 79900, annualMinor: 799000, annualMonthlyEquivalentMinor: 66583, aiBudgetMinor: 5000, extraSeatsAllowed: true, maxExtraSeats: 25 },
    { key: 'BUSINESS', name: { he: 'Business', en: 'Business' }, seats: 35, monthlyMinor: 179000, annualMinor: 1790000, annualMonthlyEquivalentMinor: 149167, aiBudgetMinor: 10000, extraSeatsAllowed: true, maxExtraSeats: 0 },
  ],
};

/** מחיר חודשי (או שווה ערך חודשי בשנתי) של בחירה, באגורות, לפני מע"מ. */
export function monthlyPriceMinor(catalog: PlanCatalog, plan: PlanKey, interval: BillingInterval, extraSeats = 0): number {
  const p = catalog.plans.find((x) => x.key === plan);
  if (!p) return 0;
  const monthly = p.monthlyMinor + (p.extraSeatsAllowed ? extraSeats * catalog.extraSeatMonthlyMinor : 0);
  return interval === 'ANNUAL' ? Math.round((monthly * catalog.annualMonthsCharged) / 12) : monthly;
}

/** אגורות → "₪1,790" / "₪299.50" (בלי מע"מ). */
export function formatMinor(minor: number, locale: string): string {
  const ils = minor / 100;
  return new Intl.NumberFormat(locale === 'en' ? 'en-IL' : 'he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: Number.isInteger(ils) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(ils);
}

/** הקטלוג לממשק המחובר (GET /billing/plans). */
export function usePlanCatalog(enabled = true) {
  return useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: ({ signal }) => request('/billing/plans', { schema: catalogSchema, signal }),
    staleTime: 60 * 60_000,
    enabled,
  });
}

/** הקטלוג לדף הנחיתה (ציבורי). נופל להעתק המקומי כשהשרת לא זמין. */
export function usePublicPlanCatalog(): PlanCatalog {
  const q = useQuery({
    queryKey: ['public', 'billing', 'plans'],
    queryFn: ({ signal }) => request('/public/billing/plans', { schema: catalogSchema, signal, tenant: null }),
    staleTime: 60 * 60_000,
    retry: false,
  });
  return q.data ?? FALLBACK_CATALOG;
}
